import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
  type ColumnFiltersState,
  type HeaderGroup,
} from "@tanstack/react-table";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Search,
  UserCheck,
  XCircle,
} from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const VALID_STATUSES: PriceStatus[] = ["RE", "CALL", "PENDING", "QUOTED"];
const VALID_MATCHES: MatchQuality[] = ["exact", "fuzzy", "manual", "unmatched"];

type StatusFilter = PriceStatus | "!QUOTED";
const VALID_STATUS_FILTERS: StatusFilter[] = [...VALID_STATUSES, "!QUOTED"];

interface PricingSearch {
  q: string;
  status: StatusFilter | undefined;
  match: MatchQuality | undefined;
}

export const Route = createFileRoute("/prototypes/pricing")({
  component: PricingWorkbench,
  validateSearch: (search: Record<string, unknown>): PricingSearch => ({
    q: typeof search.q === "string" ? search.q : "",
    status: VALID_STATUS_FILTERS.includes(search.status as StatusFilter)
      ? (search.status as StatusFilter)
      : undefined,
    match: VALID_MATCHES.includes(search.match as MatchQuality)
      ? (search.match as MatchQuality)
      : undefined,
  }),
});

// ─── Types ───────────────────────────────────────────────────────────────────

interface MockPart {
  id: string;
  nodeIds: string[];
  kind: string;
  columns: { key: string; label?: string; value: string | number | boolean }[];
  dimensions?: string;
  quantity: number;
  unit: string;
}

interface InventoryItem {
  sku: string;
  description: string;
  supplier: string;
  materialType: string;
  size?: string;
  grade?: string;
  unitPrice: number;
  stockUnit: string;
  inStock: boolean;
}

type PriceStatus = "RE" | "CALL" | "PENDING" | "QUOTED";
type MatchQuality = "exact" | "fuzzy" | "manual" | "unmatched";

interface BomLine {
  id: string;
  part: MockPart;
  inventoryMatch: InventoryItem | null;
  matchQuality: MatchQuality;
  matchConfidence?: number;
  alternateMatches?: InventoryItem[];
  status: PriceStatus;
  unitPrice: number | null;
  priceOverride: boolean;
  extendedPrice: number | null;
  expiresAt?: string;
  notes: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
}

function getPartMaterial(part: MockPart): string {
  const mat = part.columns.find(
    (c) => c.key === "material" || c.key === "species",
  );
  return mat ? String(mat.value) : part.kind;
}

function getPartSpec(
  part: MockPart,
): { key: string; label: string; value: string }[] {
  return part.columns
    .filter((c) => c.key !== "material" && c.key !== "species")
    .map((c) => ({
      key: c.key,
      label: c.label ?? c.key,
      value: String(c.value),
    }));
}

// ─── Mock Data ───────────────────────────────────────────────────────────────

const MOCK_PARTS: MockPart[] = [
  {
    id: "p-1",
    nodeIds: ["n1", "n2", "n3"],
    kind: "Lumber",
    columns: [
      { key: "material", value: "DFL #2" },
      { key: "size", label: "Size", value: "2x4" },
      { key: "length", label: "Length", value: "8'" },
    ],
    quantity: 120,
    unit: "EA",
  },
  {
    id: "p-2",
    nodeIds: ["n4", "n5"],
    kind: "Lumber",
    columns: [
      { key: "material", value: "DFL #2" },
      { key: "size", label: "Size", value: "2x4" },
      { key: "length", label: "Length", value: "16'" },
    ],
    quantity: 45,
    unit: "EA",
  },
  {
    id: "p-3",
    nodeIds: ["n6", "n7", "n8"],
    kind: "Lumber",
    columns: [
      { key: "material", value: "DFL #2" },
      { key: "size", label: "Size", value: "2x6" },
      { key: "length", label: "Length", value: "8'" },
    ],
    quantity: 88,
    unit: "EA",
  },
  {
    id: "p-4",
    nodeIds: ["n9"],
    kind: "Lumber",
    columns: [
      { key: "material", value: "DFL #2" },
      { key: "size", label: "Size", value: "2x6" },
      { key: "length", label: "Length", value: "12'" },
    ],
    quantity: 32,
    unit: "EA",
  },
  {
    id: "p-5",
    nodeIds: ["n10", "n11"],
    kind: "Lumber",
    columns: [
      { key: "material", value: "DFL #2" },
      { key: "size", label: "Size", value: "2x8" },
      { key: "length", label: "Length", value: "16'" },
    ],
    quantity: 24,
    unit: "EA",
  },
  {
    id: "p-6",
    nodeIds: ["n12"],
    kind: "Lumber",
    columns: [
      { key: "material", value: "DFL #2" },
      { key: "size", label: "Size", value: "2x10" },
      { key: "length", label: "Length", value: "12'" },
    ],
    quantity: 16,
    unit: "EA",
  },
  {
    id: "p-7",
    nodeIds: ["n13"],
    kind: "Lumber",
    columns: [
      { key: "material", value: "DFL #2" },
      { key: "size", label: "Size", value: "2x12" },
      { key: "length", label: "Length", value: "16'" },
    ],
    quantity: 8,
    unit: "EA",
  },
  {
    id: "p-8",
    nodeIds: ["n14", "n15"],
    kind: "Lumber",
    columns: [
      { key: "material", value: "DFL #2" },
      { key: "size", label: "Size", value: "4x4" },
      { key: "length", label: "Length", value: "8'" },
    ],
    quantity: 12,
    unit: "EA",
  },
  {
    id: "p-9",
    nodeIds: ["n16", "n17"],
    kind: "Panel",
    columns: [
      { key: "material", value: "CDX Plywood" },
      { key: "thickness", label: "Thickness", value: '1/2"' },
      { key: "size", label: "Size", value: "4x8" },
    ],
    quantity: 64,
    unit: "EA",
  },
  {
    id: "p-10",
    nodeIds: ["n18"],
    kind: "Panel",
    columns: [
      { key: "material", value: "CDX Plywood" },
      { key: "thickness", label: "Thickness", value: '3/4"' },
      { key: "size", label: "Size", value: "4x8" },
    ],
    quantity: 28,
    unit: "EA",
  },
  {
    id: "p-11",
    nodeIds: ["n19", "n20"],
    kind: "Panel",
    columns: [
      { key: "material", value: "OSB Sheathing" },
      { key: "thickness", label: "Thickness", value: '7/16"' },
      { key: "size", label: "Size", value: "4x8" },
    ],
    quantity: 96,
    unit: "EA",
  },
  {
    id: "p-12",
    nodeIds: ["n21"],
    kind: "Panel",
    columns: [
      { key: "material", value: "AC Plywood" },
      { key: "thickness", label: "Thickness", value: '3/4"' },
      { key: "size", label: "Size", value: "4x8" },
    ],
    quantity: 12,
    unit: "EA",
  },
  {
    id: "p-13",
    nodeIds: ["n22", "n23"],
    kind: "Drywall",
    columns: [
      { key: "material", value: 'Gypsum 1/2"' },
      { key: "type", label: "Type", value: "Standard" },
      { key: "size", label: "Size", value: "4x8" },
    ],
    quantity: 140,
    unit: "EA",
  },
  {
    id: "p-14",
    nodeIds: ["n24"],
    kind: "Drywall",
    columns: [
      { key: "material", value: 'Gypsum 5/8"' },
      { key: "type", label: "Type", value: "Type X Fire-Rated" },
      { key: "size", label: "Size", value: "4x8" },
    ],
    quantity: 48,
    unit: "EA",
  },
  {
    id: "p-15",
    nodeIds: ["n25", "n26"],
    kind: "Engineered",
    columns: [
      { key: "material", value: "TJI Joist" },
      { key: "depth", label: "Depth", value: '9.5"' },
      { key: "length", label: "Length", value: "16'" },
    ],
    quantity: 22,
    unit: "EA",
  },
  {
    id: "p-16",
    nodeIds: ["n27"],
    kind: "Finish",
    columns: [
      { key: "material", value: "WRC" },
      { key: "size", label: "Size", value: "1x6" },
      { key: "length", label: "Length", value: "8'" },
    ],
    quantity: 36,
    unit: "EA",
  },
  {
    id: "p-17",
    nodeIds: ["n28", "n29"],
    kind: "Hardware",
    columns: [
      { key: "material", value: "Simpson Strong-Tie" },
      { key: "model", label: "Model", value: "A35" },
    ],
    quantity: 64,
    unit: "EA",
  },
  {
    id: "p-18",
    nodeIds: ["n30"],
    kind: "Hardware",
    columns: [
      { key: "material", value: "Simpson Strong-Tie" },
      { key: "model", label: "Model", value: "LUS26" },
    ],
    quantity: 44,
    unit: "EA",
  },
  {
    id: "p-19",
    nodeIds: ["n31", "n32"],
    kind: "Concrete",
    columns: [
      { key: "material", value: "Ready-Mix" },
      { key: "weight", label: "Weight", value: "80 lb" },
    ],
    quantity: 30,
    unit: "BAG",
  },
  {
    id: "p-20",
    nodeIds: ["n33"],
    kind: "Fastener",
    columns: [
      { key: "material", value: "HDG 16d Common" },
      { key: "weight", label: "Weight", value: "50 lb" },
    ],
    quantity: 6,
    unit: "BOX",
  },
];

const MOCK_INVENTORY: InventoryItem[] = [
  // Pacific Lumber Supply
  {
    sku: "PLS-2408",
    description: "DF-L #2 2x4x8'",
    supplier: "Pacific Lumber Supply",
    materialType: "Lumber",
    size: "2x4",
    grade: "DF-L #2",
    unitPrice: 4.28,
    stockUnit: "EA",
    inStock: true,
  },
  {
    sku: "PLS-24016",
    description: "DF-L #2 2x4x16'",
    supplier: "Pacific Lumber Supply",
    materialType: "Lumber",
    size: "2x4",
    grade: "DF-L #2",
    unitPrice: 8.97,
    stockUnit: "EA",
    inStock: true,
  },
  {
    sku: "PLS-2608",
    description: "DF-L #2 2x6x8'",
    supplier: "Pacific Lumber Supply",
    materialType: "Lumber",
    size: "2x6",
    grade: "DF-L #2",
    unitPrice: 6.48,
    stockUnit: "EA",
    inStock: true,
  },
  {
    sku: "PLS-26012",
    description: "DF-L #2 2x6x12'",
    supplier: "Pacific Lumber Supply",
    materialType: "Lumber",
    size: "2x6",
    grade: "DF-L #2",
    unitPrice: 9.87,
    stockUnit: "EA",
    inStock: true,
  },
  {
    sku: "PLS-28016",
    description: "DF-L #2 2x8x16'",
    supplier: "Pacific Lumber Supply",
    materialType: "Lumber",
    size: "2x8",
    grade: "DF-L #2",
    unitPrice: 14.56,
    stockUnit: "EA",
    inStock: true,
  },
  {
    sku: "PLS-21012",
    description: "DF-L #2 2x10x12'",
    supplier: "Pacific Lumber Supply",
    materialType: "Lumber",
    size: "2x10",
    grade: "DF-L #2",
    unitPrice: 16.24,
    stockUnit: "EA",
    inStock: false,
  },
  {
    sku: "PLS-21216",
    description: "DF-L #2 2x12x16'",
    supplier: "Pacific Lumber Supply",
    materialType: "Lumber",
    size: "2x12",
    grade: "DF-L #2",
    unitPrice: 28.92,
    stockUnit: "EA",
    inStock: true,
  },
  {
    sku: "PLS-4408",
    description: "DF-L #2 4x4x8'",
    supplier: "Pacific Lumber Supply",
    materialType: "Lumber",
    size: "4x4",
    grade: "DF-L #2",
    unitPrice: 11.35,
    stockUnit: "EA",
    inStock: true,
  },
  {
    sku: "PLS-CDX12",
    description: 'CDX Plywood 1/2" 4x8',
    supplier: "Pacific Lumber Supply",
    materialType: "Panel",
    size: "4x8",
    unitPrice: 32.45,
    stockUnit: "EA",
    inStock: true,
  },
  {
    sku: "PLS-CDX34",
    description: 'CDX Plywood 3/4" 4x8',
    supplier: "Pacific Lumber Supply",
    materialType: "Panel",
    size: "4x8",
    unitPrice: 42.8,
    stockUnit: "EA",
    inStock: true,
  },
  {
    sku: "PLS-OSB716",
    description: 'OSB Structural 7/16" 4x8',
    supplier: "Pacific Lumber Supply",
    materialType: "Panel",
    size: "4x8",
    unitPrice: 18.95,
    stockUnit: "EA",
    inStock: true,
  },
  // BuildPro Distribution
  {
    sku: "BP-DW48-STD",
    description: '4x8 Drywall 1/2" Standard',
    supplier: "BuildPro Distribution",
    materialType: "Drywall",
    size: "4x8",
    unitPrice: 12.75,
    stockUnit: "EA",
    inStock: true,
  },
  {
    sku: "BP-DW48-FR",
    description: '4x8 Drywall 5/8" Type X Fire Rated',
    supplier: "BuildPro Distribution",
    materialType: "Drywall",
    size: "4x8",
    unitPrice: 15.4,
    stockUnit: "EA",
    inStock: true,
  },
  {
    sku: "BP-TJI950-16",
    description: "TJI 230 9-1/2\" x 16'",
    supplier: "BuildPro Distribution",
    materialType: "Engineered",
    unitPrice: 18.6,
    stockUnit: "EA",
    inStock: true,
  },
  {
    sku: "BP-A35",
    description: "Simpson Strong-Tie A35 Framing Clip",
    supplier: "BuildPro Distribution",
    materialType: "Hardware",
    unitPrice: 1.42,
    stockUnit: "EA",
    inStock: true,
  },
  {
    sku: "BP-LUS26",
    description: "Simpson Strong-Tie LUS26 Joist Hanger",
    supplier: "BuildPro Distribution",
    materialType: "Hardware",
    unitPrice: 3.85,
    stockUnit: "EA",
    inStock: true,
  },
  {
    sku: "BP-RMX80",
    description: "Quikrete 80 lb Ready-Mix Concrete",
    supplier: "BuildPro Distribution",
    materialType: "Concrete",
    unitPrice: 5.98,
    stockUnit: "BAG",
    inStock: true,
  },
  {
    sku: "BP-HDG16D",
    description: "HDG 16d Common Nails 50 lb Box",
    supplier: "BuildPro Distribution",
    materialType: "Fastener",
    unitPrice: 89.5,
    stockUnit: "BOX",
    inStock: true,
  },
  // Alternate matches
  {
    sku: "BP-2408",
    description: "Doug Fir #2 2x4 8ft",
    supplier: "BuildPro Distribution",
    materialType: "Lumber",
    size: "2x4",
    grade: "#2 DF",
    unitPrice: 4.45,
    stockUnit: "EA",
    inStock: true,
  },
  {
    sku: "BP-2608",
    description: "Doug Fir #2 2x6 8ft",
    supplier: "BuildPro Distribution",
    materialType: "Lumber",
    size: "2x6",
    grade: "#2 DF",
    unitPrice: 6.72,
    stockUnit: "EA",
    inStock: true,
  },
  {
    sku: "BP-CDX12",
    description: "CDX Ply 1/2 4'x8'",
    supplier: "BuildPro Distribution",
    materialType: "Panel",
    size: "4x8",
    unitPrice: 31.5,
    stockUnit: "EA",
    inStock: true,
  },
];

// ─── Match Engine ────────────────────────────────────────────────────────────

type MatchResult = {
  item: InventoryItem;
  quality: MatchQuality;
  confidence: number;
};

function matchPartToInventory(part: MockPart): MatchResult[] {
  const material = getPartMaterial(part).toLowerCase();
  const size = part.columns
    .find((c) => c.key === "size")
    ?.value?.toString()
    .toLowerCase();
  const kind = part.kind.toLowerCase();

  const scored: MatchResult[] = [];

  for (const item of MOCK_INVENTORY) {
    let score = 0;
    const desc = item.description.toLowerCase();
    const matType = item.materialType.toLowerCase();

    // Kind / materialType match
    if (matType === kind) score += 30;

    // Size match
    if (size && item.size?.toLowerCase() === size) score += 25;
    if (size && desc.includes(size)) score += 15;

    // Material / description similarity
    if (
      material.includes("dfl") &&
      (desc.includes("df-l") || desc.includes("doug fir"))
    )
      score += 20;
    if (material.includes("cdx") && desc.includes("cdx")) score += 25;
    if (material.includes("osb") && desc.includes("osb")) score += 25;
    if (material.includes("gypsum") && matType === "drywall") score += 20;
    if (material.includes("tji") && desc.includes("tji")) score += 25;
    if (material.includes("simpson") && desc.includes("simpson")) score += 20;
    if (material.includes("ready-mix") && desc.includes("ready-mix"))
      score += 25;
    if (material.includes("hdg") && desc.includes("hdg")) score += 25;
    if (material.includes("wrc") && desc.includes("wrc")) score += 25;

    // Model / specific item match
    const model = part.columns
      .find((c) => c.key === "model")
      ?.value?.toString();
    if (model && desc.toLowerCase().includes(model.toLowerCase())) score += 25;

    // Length match
    const length = part.columns
      .find((c) => c.key === "length")
      ?.value?.toString();
    if (length && desc.includes(length.replace("'", "'"))) score += 15;
    if (length && desc.includes(length.replace("'", "ft"))) score += 15;

    // Thickness match
    const thickness = part.columns
      .find((c) => c.key === "thickness")
      ?.value?.toString();
    if (thickness && desc.includes(thickness.replace('"', '"'))) score += 15;
    if (thickness && desc.toLowerCase().includes(thickness.replace('"', "")))
      score += 10;

    // Type match (fire-rated, standard)
    const type = part.columns.find((c) => c.key === "type")?.value?.toString();
    if (type && desc.toLowerCase().includes(type.toLowerCase().split(" ")[0]))
      score += 15;

    // Unit match
    if (item.stockUnit === part.unit) score += 5;

    if (score >= 30) {
      const quality: MatchQuality =
        score >= 70 ? "exact" : score >= 45 ? "fuzzy" : "fuzzy";
      scored.push({ item, quality, confidence: Math.min(score, 100) });
    }
  }

  scored.sort((a, b) => b.confidence - a.confidence);
  return scored;
}

function buildInitialBomLines(): BomLine[] {
  return MOCK_PARTS.map((part) => {
    const matches = matchPartToInventory(part);
    const best = matches[0];

    if (!best) {
      return {
        id: part.id,
        part,
        inventoryMatch: null,
        matchQuality: "unmatched" as const,
        alternateMatches: [],
        status: "CALL" as const,
        unitPrice: null,
        priceOverride: false,
        extendedPrice: null,
        notes: "",
      };
    }

    return {
      id: part.id,
      part,
      inventoryMatch: best.item,
      matchQuality: best.quality,
      matchConfidence: best.confidence,
      alternateMatches: matches.slice(1).map((m) => m.item),
      status: (best.quality === "exact" ? "QUOTED" : "RE") as PriceStatus,
      unitPrice: best.item.unitPrice,
      priceOverride: false,
      extendedPrice: part.quantity * best.item.unitPrice,
      notes: "",
    };
  });
}

// Manually override a couple lines for variety
function applyManualOverrides(lines: BomLine[]): BomLine[] {
  return lines.map((line) => {
    // Make AC Plywood unmatched
    if (line.part.id === "p-12") {
      return {
        ...line,
        inventoryMatch: null,
        matchQuality: "unmatched" as const,
        alternateMatches: [],
        status: "CALL" as const,
        unitPrice: null,
        extendedPrice: null,
      };
    }
    // Make WRC finish unmatched
    if (line.part.id === "p-16") {
      return {
        ...line,
        inventoryMatch: null,
        matchQuality: "unmatched" as const,
        alternateMatches: [],
        status: "CALL" as const,
        unitPrice: null,
        extendedPrice: null,
      };
    }
    // Make 4x4 a manual match
    if (line.part.id === "p-8") {
      return {
        ...line,
        matchQuality: "manual" as const,
        status: "QUOTED" as const,
      };
    }
    // Make 2x10 PENDING
    if (line.part.id === "p-6") {
      return {
        ...line,
        status: "PENDING" as const,
      };
    }
    // Make 2x12 have a note
    if (line.part.id === "p-7") {
      return {
        ...line,
        notes: "Check availability — may need special order",
      };
    }
    return line;
  });
}

// ─── Leaf Components ─────────────────────────────────────────────────────────

const STATUS_COLORS: Record<PriceStatus, string> = {
  RE: "bg-gray-100 text-gray-700 border-gray-300",
  CALL: "bg-amber-50 text-amber-700 border-amber-300",
  PENDING: "bg-blue-50 text-blue-700 border-blue-300",
  QUOTED: "bg-emerald-50 text-emerald-700 border-emerald-300",
};

const STATUS_LABELS: Record<PriceStatus, string> = {
  RE: "Rough Est.",
  CALL: "Call",
  PENDING: "Pending",
  QUOTED: "Quoted",
};

function StatusBadge({
  status,
  onClick,
}: {
  status: PriceStatus;
  onClick?: () => void;
}) {
  return (
    <span
      className={cn(
        "inline-flex cursor-pointer items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        STATUS_COLORS[status],
      )}
      onClick={onClick}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

function StatusSelect({
  value,
  onChange,
  onClose,
}: {
  value: PriceStatus;
  onChange: (v: PriceStatus) => void;
  onClose: () => void;
}) {
  return (
    <Select
      value={value}
      onValueChange={(v) => {
        onChange(v as PriceStatus);
        onClose();
      }}
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SelectTrigger className="h-7 w-[110px] text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="RE">RE — Rough Est.</SelectItem>
        <SelectItem value="CALL">CALL — Call Supplier</SelectItem>
        <SelectItem value="PENDING">PENDING — Awaiting</SelectItem>
        <SelectItem value="QUOTED">QUOTED — Firm</SelectItem>
      </SelectContent>
    </Select>
  );
}

function MatchIndicator({
  quality,
  confidence,
  sku,
  alternates,
  onSelectAlternate,
}: {
  quality: MatchQuality;
  confidence?: number;
  sku?: string;
  alternates?: InventoryItem[];
  onSelectAlternate?: (item: InventoryItem) => void;
}) {
  if (quality === "exact") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 text-emerald-600">
            <CheckCircle2 className="h-4 w-4" />
          </span>
        </TooltipTrigger>
        <TooltipContent>Exact match: {sku}</TooltipContent>
      </Tooltip>
    );
  }

  if (quality === "fuzzy") {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-xs">{confidence}%</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80">
          <div className="space-y-2">
            <p className="text-sm font-medium">
              Fuzzy match ({confidence}% confidence)
            </p>
            <p className="text-xs text-muted-foreground">Current: {sku}</p>
            {alternates && alternates.length > 0 && (
              <>
                <p className="text-xs font-medium pt-1">Alternatives:</p>
                <div className="space-y-1">
                  {alternates.map((alt) => (
                    <button
                      key={alt.sku}
                      className="flex w-full items-center justify-between rounded-md border px-2 py-1.5 text-xs hover:bg-muted"
                      onClick={() => onSelectAlternate?.(alt)}
                    >
                      <div>
                        <span className="font-mono">{alt.sku}</span>
                        <span className="ml-2 text-muted-foreground">
                          {alt.description}
                        </span>
                      </div>
                      <span className="font-medium">{fmt(alt.unitPrice)}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  if (quality === "manual") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 text-blue-600">
            <UserCheck className="h-4 w-4" />
          </span>
        </TooltipTrigger>
        <TooltipContent>Manually matched: {sku}</TooltipContent>
      </Tooltip>
    );
  }

  // unmatched
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center gap-1 text-red-500 hover:text-red-600">
          <XCircle className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <div className="space-y-2">
          <p className="text-sm font-medium text-red-600">No match found</p>
          <p className="text-xs text-muted-foreground">
            Search or manually assign an inventory item.
          </p>
          <Input placeholder="Search inventory..." className="h-7 text-xs" />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function EditablePrice({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = useCallback(() => {
    setDraft(value != null ? value.toFixed(2) : "");
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  }, [value]);

  const commit = useCallback(() => {
    setEditing(false);
    const parsed = parseFloat(draft);
    if (!isNaN(parsed) && parsed >= 0) {
      onChange(parsed);
    }
  }, [draft, onChange]);

  const cancel = useCallback(() => {
    setEditing(false);
  }, []);

  if (editing) {
    return (
      <Input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") cancel();
        }}
        className="h-7 w-24 text-right text-xs font-mono"
        autoFocus
      />
    );
  }

  return (
    <span
      className="cursor-pointer rounded px-1 py-0.5 text-right font-mono text-xs hover:bg-muted"
      onClick={startEditing}
    >
      {value != null ? fmt(value) : "—"}
    </span>
  );
}

function EditableNote({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = useCallback(() => {
    setDraft(value);
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [value]);

  const commit = useCallback(() => {
    setEditing(false);
    onChange(draft);
  }, [draft, onChange]);

  const cancel = useCallback(() => {
    setEditing(false);
  }, []);

  if (editing) {
    return (
      <Input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") cancel();
        }}
        className="h-7 w-full text-xs"
        autoFocus
      />
    );
  }

  return (
    <span
      className={cn(
        "block cursor-pointer truncate rounded px-1 py-0.5 text-xs hover:bg-muted",
        !value && "text-muted-foreground italic",
      )}
      onClick={startEditing}
    >
      {value || "Add note..."}
    </span>
  );
}

function SpecChips({
  specs,
}: {
  specs: { key: string; label: string; value: string }[];
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {specs.map((s) => (
        <Badge
          key={s.key}
          variant="secondary"
          className="px-1.5 py-0 text-[10px] font-normal"
        >
          {s.value}
        </Badge>
      ))}
    </div>
  );
}

// ─── Column Definitions ──────────────────────────────────────────────────────

const columnHelper = createColumnHelper<BomLine>();

function getRowBgClass(quality: MatchQuality): string {
  switch (quality) {
    case "fuzzy":
      return "bg-amber-50/50";
    case "manual":
      return "bg-blue-50/50";
    case "unmatched":
      return "bg-red-50/50";
    default:
      return "";
  }
}

function buildColumns(
  onUpdateLine: (id: string, updates: Partial<BomLine>) => void,
  onSelectAlternate: (id: string, item: InventoryItem) => void,
) {
  return [
    columnHelper.group({
      id: "parts",
      header: "Parts",
      columns: [
        columnHelper.accessor((row) => row.part.kind, {
          id: "division",
          header: "Division",
          size: 90,
        }),
        columnHelper.accessor((row) => getPartMaterial(row.part), {
          id: "material",
          header: "Material",
          size: 120,
        }),
        columnHelper.display({
          id: "spec",
          header: "Spec",
          size: 180,
          cell: ({ row }) => (
            <SpecChips specs={getPartSpec(row.original.part)} />
          ),
        }),
        columnHelper.accessor((row) => row.part.quantity, {
          id: "qty",
          header: "Qty",
          size: 60,
          cell: ({ getValue }) => (
            <span className="font-mono text-xs">{getValue()}</span>
          ),
        }),
        columnHelper.accessor((row) => row.part.unit, {
          id: "unit",
          header: "Unit",
          size: 50,
          cell: ({ getValue }) => (
            <span className="text-xs text-muted-foreground">{getValue()}</span>
          ),
        }),
      ],
    }),
    columnHelper.group({
      id: "inventory",
      header: "Inventory",
      columns: [
        columnHelper.display({
          id: "match",
          header: "Match",
          size: 70,
          cell: ({ row }) => (
            <MatchIndicator
              quality={row.original.matchQuality}
              confidence={row.original.matchConfidence}
              sku={row.original.inventoryMatch?.sku}
              alternates={row.original.alternateMatches}
              onSelectAlternate={(item) =>
                onSelectAlternate(row.original.id, item)
              }
            />
          ),
        }),
        columnHelper.accessor((row) => row.inventoryMatch?.sku ?? "", {
          id: "sku",
          header: "SKU",
          size: 100,
          cell: ({ getValue }) => (
            <span className="font-mono text-xs">{getValue() || "—"}</span>
          ),
        }),
        columnHelper.accessor((row) => row.inventoryMatch?.supplier ?? "", {
          id: "supplier",
          header: "Supplier",
          size: 140,
          cell: ({ getValue }) => (
            <span className="text-xs">{getValue() || "—"}</span>
          ),
        }),
        columnHelper.accessor((row) => row.inventoryMatch?.description ?? "", {
          id: "description",
          header: "Description",
          size: 160,
          cell: ({ getValue }) => (
            <span className="text-xs">{getValue() || "—"}</span>
          ),
        }),
      ],
    }),
    columnHelper.group({
      id: "pricing",
      header: "Pricing",
      columns: [
        columnHelper.display({
          id: "status",
          header: "Status",
          size: 90,
          cell: function StatusCell({ row }) {
            const [editing, setEditing] = useState(false);
            const line = row.original;
            if (editing) {
              return (
                <StatusSelect
                  value={line.status}
                  onChange={(v) => onUpdateLine(line.id, { status: v })}
                  onClose={() => setEditing(false)}
                />
              );
            }
            return (
              <StatusBadge
                status={line.status}
                onClick={() => setEditing(true)}
              />
            );
          },
        }),
        columnHelper.display({
          id: "unitPrice",
          header: "Unit Price",
          size: 100,
          cell: ({ row }) => (
            <EditablePrice
              value={row.original.unitPrice}
              onChange={(v) =>
                onUpdateLine(row.original.id, {
                  unitPrice: v,
                  priceOverride: true,
                  extendedPrice:
                    v != null ? row.original.part.quantity * v : null,
                })
              }
            />
          ),
        }),
        columnHelper.display({
          id: "extPrice",
          header: "Ext. Price",
          size: 100,
          cell: ({ row }) => (
            <span className="font-mono text-xs">
              {row.original.extendedPrice != null
                ? fmt(row.original.extendedPrice)
                : "—"}
            </span>
          ),
        }),
        columnHelper.display({
          id: "notes",
          header: "Notes",
          size: 180,
          cell: ({ row }) => (
            <EditableNote
              value={row.original.notes}
              onChange={(v) => onUpdateLine(row.original.id, { notes: v })}
            />
          ),
        }),
      ],
    }),
  ];
}

// ─── Group Header Colors ─────────────────────────────────────────────────────

const GROUP_COLORS: Record<string, string> = {
  parts: "bg-blue-600 text-white",
  inventory: "bg-amber-500 text-white",
  pricing: "bg-emerald-600 text-white",
};

// ─── BomTable ────────────────────────────────────────────────────────────────

function BomTable({
  data,
  onUpdateLine,
  onSelectAlternate,
  searchQuery,
  statusFilter,
  matchFilter,
}: {
  data: BomLine[];
  onUpdateLine: (id: string, updates: Partial<BomLine>) => void;
  onSelectAlternate: (id: string, item: InventoryItem) => void;
  searchQuery: string;
  statusFilter: StatusFilter | null;
  matchFilter: MatchQuality | null;
}) {
  const columns = useMemo(
    () => buildColumns(onUpdateLine, onSelectAlternate),
    [onUpdateLine, onSelectAlternate],
  );

  const filteredData = useMemo(() => {
    let result = data;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (line) =>
          getPartMaterial(line.part).toLowerCase().includes(q) ||
          line.part.kind.toLowerCase().includes(q) ||
          line.inventoryMatch?.sku.toLowerCase().includes(q) ||
          line.inventoryMatch?.supplier.toLowerCase().includes(q) ||
          line.inventoryMatch?.description.toLowerCase().includes(q) ||
          line.part.columns.some((c) =>
            String(c.value).toLowerCase().includes(q),
          ),
      );
    }
    if (statusFilter) {
      if (statusFilter.startsWith("!")) {
        const negated = statusFilter.slice(1);
        result = result.filter((line) => line.status !== negated);
      } else {
        result = result.filter((line) => line.status === statusFilter);
      }
    }
    if (matchFilter) {
      result = result.filter((line) => line.matchQuality === matchFilter);
    }
    return result;
  }, [data, searchQuery, statusFilter, matchFilter]);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="overflow-auto rounded-md border">
      <table className="w-full text-sm">
        <thead>
          {table.getHeaderGroups().map((headerGroup: HeaderGroup<BomLine>) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const groupColor =
                  GROUP_COLORS[header.id] ||
                  GROUP_COLORS[header.column.parent?.id ?? ""];
                const isGroupHeader =
                  header.depth === 0 && header.subHeaders.length > 0;
                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className={cn(
                      "px-2 py-1.5 text-left text-xs font-medium",
                      isGroupHeader
                        ? cn(
                            groupColor,
                            "text-center font-semibold tracking-wide",
                          )
                        : "border-b bg-muted/30 text-muted-foreground",
                    )}
                    style={
                      !isGroupHeader && header.getSize() !== 150
                        ? { width: header.getSize() }
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={cn(
                "border-b transition-colors hover:bg-muted/50",
                getRowBgClass(row.original.matchQuality),
              )}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-2 py-1.5">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
          {table.getRowModel().rows.length === 0 && (
            <tr>
              <td
                colSpan={columns.reduce(
                  (sum, g) =>
                    sum + ("columns" in g ? (g.columns?.length ?? 0) : 1),
                  0,
                )}
                className="py-8 text-center text-sm text-muted-foreground"
              >
                No matching items found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── PricingToolbar ──────────────────────────────────────────────────────────

function PricingToolbar({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  matchFilter,
  onMatchFilterChange,
}: {
  searchQuery: string;
  onSearchChange: (v: string) => void;
  statusFilter: StatusFilter | null;
  onStatusFilterChange: (v: StatusFilter | null) => void;
  matchFilter: MatchQuality | null;
  onMatchFilterChange: (v: MatchQuality | null) => void;
}) {
  const statuses: PriceStatus[] = ["RE", "CALL", "PENDING", "QUOTED"];
  const matchQualities: { value: MatchQuality; label: string }[] = [
    { value: "exact", label: "Exact" },
    { value: "fuzzy", label: "Fuzzy" },
    { value: "manual", label: "Manual" },
    { value: "unmatched", label: "Unmatched" },
  ];

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card px-4 py-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search materials, SKUs..."
          className="h-8 w-64 pl-8 text-sm"
        />
      </div>
      <div className="h-5 w-px bg-border" />
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-muted-foreground">Status:</span>
        {statuses.map((s) => (
          <button
            key={s}
            onClick={() => onStatusFilterChange(statusFilter === s ? null : s)}
            className={cn(
              "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium transition-colors",
              statusFilter === s
                ? STATUS_COLORS[s]
                : "border-transparent text-muted-foreground hover:bg-muted",
            )}
          >
            {s}
          </button>
        ))}
      </div>
      <div className="h-5 w-px bg-border" />
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-muted-foreground">Match:</span>
        {matchQualities.map((m) => (
          <button
            key={m.value}
            onClick={() =>
              onMatchFilterChange(matchFilter === m.value ? null : m.value)
            }
            className={cn(
              "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium transition-colors",
              matchFilter === m.value
                ? "border-foreground/20 bg-foreground/5 text-foreground"
                : "border-transparent text-muted-foreground hover:bg-muted",
            )}
          >
            {m.label}
          </button>
        ))}
      </div>
      <div className="ml-auto">
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
          <Download className="h-3.5 w-3.5" />
          Export
        </Button>
      </div>
    </div>
  );
}

// ─── PricingSummary ──────────────────────────────────────────────────────────

function useStats(data: BomLine[]) {
  return useMemo(() => {
    const total = data.length;
    const unmatched = data.filter((l) => l.matchQuality === "unmatched").length;
    const fuzzy = data.filter((l) => l.matchQuality === "fuzzy").length;
    const quoted = data.filter((l) => l.status === "QUOTED").length;
    const subtotal = data.reduce((sum, l) => sum + (l.extendedPrice ?? 0), 0);
    return { total, unmatched, fuzzy, quoted, subtotal };
  }, [data]);
}

function HeaderStats({ data }: { data: BomLine[] }) {
  const stats = useStats(data);
  const { match, status } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const toggleMatch = (m: MatchQuality) =>
    navigate({
      search: (prev) => ({ ...prev, match: match === m ? undefined : m }),
    });
  const toggleStatus = (s: StatusFilter) =>
    navigate({
      search: (prev) => ({ ...prev, status: status === s ? undefined : s }),
    });

  return (
    <div className="flex items-center gap-4 text-sm">
      <span className="text-muted-foreground">{stats.total} items</span>
      {stats.unmatched > 0 && (
        <button
          onClick={() => toggleMatch("unmatched")}
          className={cn(
            "font-medium text-red-600 hover:underline",
            match === "unmatched" && "underline",
          )}
        >
          {stats.unmatched} unmatched
        </button>
      )}
      {stats.fuzzy > 0 && (
        <button
          onClick={() => toggleMatch("fuzzy")}
          className={cn(
            "font-medium text-amber-600 hover:underline",
            match === "fuzzy" && "underline",
          )}
        >
          {stats.fuzzy} fuzzy
        </button>
      )}
      <button
        onClick={() => toggleStatus("!QUOTED")}
        className={cn(
          "text-muted-foreground hover:underline",
          status === "!QUOTED" && "underline",
        )}
      >
        {stats.quoted}/{stats.total} quoted
      </button>
    </div>
  );
}

function PricingSummary({ data }: { data: BomLine[] }) {
  const stats = useStats(data);
  return (
    <div className="flex items-center justify-end rounded-lg border bg-card px-4 py-3">
      <div className="text-sm">
        <span className="text-muted-foreground">Subtotal: </span>
        <span className="text-lg font-semibold">{fmt(stats.subtotal)}</span>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

function PricingWorkbench() {
  const { q, status, match } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const setSearch = useCallback(
    (updates: Partial<PricingSearch>) =>
      navigate({ search: (prev) => ({ ...prev, ...updates }) }),
    [navigate],
  );

  const [lines, setLines] = useState<BomLine[]>(() =>
    applyManualOverrides(buildInitialBomLines()),
  );

  const handleUpdateLine = useCallback(
    (id: string, updates: Partial<BomLine>) => {
      setLines((prev) =>
        prev.map((line) => {
          if (line.id !== id) return line;
          const updated = { ...line, ...updates };
          if ("unitPrice" in updates) {
            updated.extendedPrice =
              updated.unitPrice != null
                ? updated.part.quantity * updated.unitPrice
                : null;
          }
          return updated;
        }),
      );
    },
    [],
  );

  const handleSelectAlternate = useCallback(
    (lineId: string, item: InventoryItem) => {
      setLines((prev) =>
        prev.map((line) => {
          if (line.id !== lineId) return line;
          return {
            ...line,
            inventoryMatch: item,
            matchQuality: "manual" as const,
            matchConfidence: undefined,
            unitPrice: item.unitPrice,
            priceOverride: false,
            extendedPrice: line.part.quantity * item.unitPrice,
            status: "RE" as const,
          };
        }),
      );
    },
    [],
  );

  return (
    <TooltipProvider>
      <div className="flex min-h-screen flex-col bg-background">
        {/* Header */}
        <div className="border-b bg-card px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                Pricing Workbench
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Demo Project — Garage ADU
              </p>
            </div>
            <HeaderStats data={lines} />
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col gap-3 p-4">
          <PricingToolbar
            searchQuery={q}
            onSearchChange={(v) => setSearch({ q: v || "" })}
            statusFilter={status ?? null}
            onStatusFilterChange={(v) => setSearch({ status: v ?? undefined })}
            matchFilter={match ?? null}
            onMatchFilterChange={(v) => setSearch({ match: v ?? undefined })}
          />

          <BomTable
            data={lines}
            onUpdateLine={handleUpdateLine}
            onSelectAlternate={handleSelectAlternate}
            searchQuery={q}
            statusFilter={status ?? null}
            matchFilter={match ?? null}
          />

          <PricingSummary data={lines} />
        </div>
      </div>
    </TooltipProvider>
  );
}
